import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ALARMS, getAlarm, setAlarm, playAlarm } from "@/lib/alarm";
import { Pill, Plus, Check, Clock, X, Sun, Sunset, Moon, HeartPulse, Trash2, ShieldCheck, Volume2, VolumeX, Play } from "lucide-react";

const SLOTS = [
  { key: "matin", label: "Matin", icon: Sun },
  { key: "midi", label: "Midi", icon: Sunset },
  { key: "soir", label: "Soir", icon: Moon },
];
const STATUS_STYLE = {
  taken: { label: "Pris", color: "#10B981" },
  snoozed: { label: "Reporté", color: "#F59E0B" },
  skipped: { label: "Ignoré", color: "#6B7280" },
};

export default function Sante() {
  const [meds, setMeds] = useState([]);
  const [today, setToday] = useState({ doses: [] });
  const [form, setForm] = useState({ name: "", dosage: "", times: [], duration_days: "" });
  const [voiceOn, setVoiceOn] = useState(localStorage.getItem("omy_health_voice") !== "0");
  const [alarmId, setAlarmId] = useState(getAlarm());

  const load = () => {
    api.get("/health/medications").then((r) => setMeds(r.data));
    api.get("/health/today").then((r) => setToday(r.data));
  };
  useEffect(() => { load(); }, []);

  const toggleTime = (t) => setForm((f) => ({ ...f, times: f.times.includes(t) ? f.times.filter((x) => x !== t) : [...f.times, t] }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nom du médicament requis");
    if (form.times.length === 0) return toast.error("Choisis au moins un moment");
    await api.post("/health/medications", { ...form, duration_days: form.duration_days ? parseInt(form.duration_days) : null });
    toast.success("Rappel santé ajouté");
    setForm({ name: "", dosage: "", times: [], duration_days: "" });
    load();
  };
  const del = async (id) => { await api.delete(`/health/medications/${id}`); toast.success("Supprimé"); load(); };
  const logDose = async (mid, slot, status) => {
    await api.post(`/health/medications/${mid}/log`, { slot, status });
    toast.success(STATUS_STYLE[status].label);
    load();
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight flex items-center gap-2"><HeartPulse className="text-accent" /> Santé</h1>
        <p className="text-muted-foreground text-sm mt-1">Module isolé et protégé. OMY te rappelle tes prises selon TES instructions — il ne prescrit rien et ne donne aucun conseil médical.</p>
      </div>

      <div className="omy-glass rounded-2xl p-6">
        <div className="flex items-center justify-between gap-2 mb-4">
          <h2 className="font-display font-medium flex items-center gap-2"><Clock size={18} className="text-primary" /> Aujourd'hui</h2>
          <button data-testid="health-voice-toggle" title="OMY te rappelle tes prises à voix haute (matin 8h · midi 12h30 · soir 20h)"
            onClick={() => { const n = !voiceOn; setVoiceOn(n); localStorage.setItem("omy_health_voice", n ? "1" : "0"); toast.success(n ? "Rappels vocaux activés" : "Rappels vocaux désactivés"); }}
            className={`flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5 transition-colors shrink-0 ${voiceOn ? "bg-primary/15 text-primary" : "bg-white/5 text-muted-foreground hover:text-foreground"}`}>
            {voiceOn ? <Volume2 size={14} /> : <VolumeX size={14} />} Voix {voiceOn ? "ON" : "OFF"}
          </button>
        </div>
        {voiceOn && (
          <div className="flex items-center gap-2 mb-4 -mt-1">
            <span className="text-xs text-muted-foreground shrink-0">Son d'alerte :</span>
            <Select value={alarmId} onValueChange={(v) => { setAlarmId(v); setAlarm(v); playAlarm(v); }}>
              <SelectTrigger data-testid="alarm-select" className="h-8 w-[130px] bg-black/30 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ALARMS.map((a) => <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <button data-testid="alarm-preview" onClick={() => playAlarm(alarmId)} title="Écouter le son"
              className="w-8 h-8 rounded-full bg-primary/15 text-primary flex items-center justify-center hover:bg-primary/25 shrink-0"><Play size={13} className="ml-0.5" /></button>
          </div>
        )}
        {today.doses.length === 0 && <p className="text-sm text-muted-foreground">Aucune prise prévue aujourd'hui.</p>}
        <div className="space-y-3">
          {today.doses.map((d, i) => {
            const slot = SLOTS.find((s) => s.key === d.slot) || SLOTS[0];
            const SlotIcon = slot.icon;
            const st = d.status ? STATUS_STYLE[d.status] : null;
            return (
              <div key={i} data-testid={`dose-${i}`} className="flex items-center gap-3 rounded-xl bg-black/20 px-4 py-3">
                <SlotIcon size={18} className="text-accent shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{d.name} <span className="text-xs text-muted-foreground">· {slot.label}</span></p>
                  {d.dosage && <p className="text-xs text-muted-foreground">{d.dosage}</p>}
                </div>
                {st ? (
                  <span className="text-xs font-medium px-2.5 py-1 rounded-full shrink-0" style={{ color: st.color, background: `${st.color}18` }}>{st.label}</span>
                ) : (
                  <div className="flex gap-1.5 shrink-0">
                    <button data-testid={`dose-taken-${i}`} onClick={() => logDose(d.medication_id, d.slot, "taken")} title="Pris"
                      className="w-8 h-8 rounded-full bg-emerald-500/15 text-emerald-400 flex items-center justify-center hover:bg-emerald-500/25"><Check size={16} /></button>
                    <button data-testid={`dose-snoozed-${i}`} onClick={() => logDose(d.medication_id, d.slot, "snoozed")} title="Reporter"
                      className="w-8 h-8 rounded-full bg-amber-500/15 text-amber-400 flex items-center justify-center hover:bg-amber-500/25"><Clock size={16} /></button>
                    <button data-testid={`dose-skipped-${i}`} onClick={() => logDose(d.medication_id, d.slot, "skipped")} title="Ignorer"
                      className="w-8 h-8 rounded-full bg-white/5 text-muted-foreground flex items-center justify-center hover:bg-white/10"><X size={16} /></button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="omy-glass rounded-2xl p-6 space-y-4">
        <h2 className="font-display font-medium flex items-center gap-2"><Plus size={18} className="text-primary" /> Ajouter un traitement</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Médicament</Label><Input data-testid="med-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-black/30" placeholder="Doliprane 1000" /></div>
          <div><Label>Posologie</Label><Input data-testid="med-dosage-input" value={form.dosage} onChange={(e) => setForm({ ...form, dosage: e.target.value })} className="mt-1 bg-black/30" placeholder="1 comprimé" /></div>
        </div>
        <div>
          <Label>Moments de prise</Label>
          <div className="flex gap-4 mt-2">
            {SLOTS.map((s) => (
              <label key={s.key} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox data-testid={`med-time-${s.key}`} checked={form.times.includes(s.key)} onCheckedChange={() => toggleTime(s.key)} />
                <s.icon size={15} className="text-accent" /> {s.label}
              </label>
            ))}
          </div>
        </div>
        <div className="max-w-xs"><Label>Durée (jours)</Label><Input data-testid="med-duration-input" type="number" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: e.target.value })} className="mt-1 bg-black/30" placeholder="7" /></div>
        <Button data-testid="med-save-btn" onClick={save} className="bg-primary hover:bg-primary/90 rounded-full">Enregistrer</Button>
      </div>

      {meds.length > 0 && (
        <div className="space-y-3">
          <h2 className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Mes traitements</h2>
          {meds.map((m, i) => (
            <div key={m.id} data-testid={`med-${i}`} className="omy-glass rounded-2xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-accent/15 flex items-center justify-center shrink-0"><Pill size={18} className="text-accent" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{m.name}</p>
                <p className="text-xs text-muted-foreground">{m.dosage} · {(m.times || []).join(", ")}{m.duration_days ? ` · ${m.duration_days} j` : ""}</p>
              </div>
              <button data-testid={`med-delete-${i}`} onClick={() => del(m.id)} className="text-muted-foreground hover:text-destructive shrink-0"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-muted-foreground flex items-center gap-1.5"><ShieldCheck size={12} className="text-accent" /> Données de santé isolées et chiffrées. OMY ne diagnostique pas et ne modifie jamais un traitement.</p>
    </div>
  );
}
